import React, { useState } from 'react';
import { 
  Plus, 
  Search, 
  MessageSquare, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  User, 
  Mail, 
  Calendar, 
  FileText,
  Copy,
  ArrowUpDown,
  Eye,
  Edit,
  Trash2,
  Save,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from '@/hooks/use-toast';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

// Interface cho Ticket
interface Ticket {
  id: string;
  title: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  relatedInfo?: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  createdBy: string;
  assignedTo?: string;
  comments?: Comment[];
}

interface Comment {
  id: string;
  content: string;
  author: string;
  createdAt: string;
}

const Support = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // State cho CRUD operations
  const [tickets, setTickets] = useState<Ticket[]>([
    {
      id: 'TK001',
      title: 'Đơn hàng DH001 bị trễ giao',
      description: 'Đơn hàng của tôi đã quá hạn giao nhưng vẫn chưa nhận được',
      category: 'Giao hàng',
      priority: 'Cao',
      status: 'Đang xử lý',
      relatedInfo: 'DH001',
      createdAt: '2025-01-12T10:30:00+07:00',
      updatedAt: '2025-01-12T14:20:00+07:00',
      createdBy: 'Nguyễn Văn A',
      assignedTo: 'Nhân viên hỗ trợ 1',
      comments: [
        {
          id: 'C001',
          content: 'Đã liên hệ với đơn vị vận chuyển để kiểm tra tình trạng',
          author: 'Nhân viên hỗ trợ 1',
          createdAt: '2025-01-12T11:00:00+07:00'
        }
      ]
    },
    {
      id: 'TK002',
      title: 'Không thể tạo đơn hàng mới',
      description: 'Hệ thống báo lỗi khi tôi cố gắng tạo đơn hàng mới từ tài khoản',
      category: 'Kỹ thuật',
      priority: 'Trung bình',
      status: 'Hoàn thành',
      relatedInfo: 'Tài khoản KH001',
      createdAt: '2025-01-10T09:15:00+07:00',
      updatedAt: '2025-01-11T16:45:00+07:00',
      resolvedAt: '2025-01-11T16:45:00+07:00',
      createdBy: 'Trần Thị B',
      assignedTo: 'Kỹ thuật viên',
      comments: [
        {
          id: 'C002',
          content: 'Đã khắc phục lỗi cache trình duyệt',
          author: 'Kỹ thuật viên',
          createdAt: '2025-01-11T16:45:00+07:00'
        }
      ]
    },
    {
      id: 'TK003',
      title: 'Yêu cầu thay đổi thông tin liên hệ',
      description: 'Cần cập nhật số điện thoại và địa chỉ trong hệ thống',
      category: 'Tài khoản',
      priority: 'Thấp',
      status: 'Mới',
      relatedInfo: 'Cập nhật profile',
      createdAt: '2025-01-13T08:20:00+07:00',
      updatedAt: '2025-01-13T08:20:00+07:00',
      createdBy: 'Lê Văn C'
    }
  ]);

  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);

  // Form states
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    category: '',
    priority: '',
    relatedInfo: ''
  });

  const [editTicket, setEditTicket] = useState({
    title: '',
    description: '',
    category: '',
    priority: '',
    status: '',
    relatedInfo: '',
    assignedTo: ''
  });

  const [newComment, setNewComment] = useState('');

  // CRUD Functions
  const createTicket = () => {
    if (!newTicket.title || !newTicket.description || !newTicket.category || !newTicket.priority) {
      toast({
        title: "Lỗi",
        description: "Vui lòng điền đầy đủ thông tin bắt buộc",
        variant: "destructive"
      });
      return;
    }

    const ticket: Ticket = {
      id: `TK${String(tickets.length + 1).padStart(3, '0')}`,
      title: newTicket.title,
      description: newTicket.description,
      category: newTicket.category,
      priority: newTicket.priority,
      status: 'Mới',
      relatedInfo: newTicket.relatedInfo,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'Nguyễn Văn A',
      comments: []
    };

    setTickets([...tickets, ticket]);
    setNewTicket({ title: '', description: '', category: '', priority: '', relatedInfo: '' });
    setShowCreateDialog(false);
    
    toast({
      title: "Thành công",
      description: "Đã tạo yêu cầu hỗ trợ mới",
    });
  };

  const updateTicket = () => {
    if (!selectedTicket) return;

    const updatedTickets = tickets.map(ticket => 
      ticket.id === selectedTicket.id 
        ? { 
            ...ticket, 
            ...editTicket,
            updatedAt: new Date().toISOString(),
            resolvedAt: editTicket.status === 'Hoàn thành' ? new Date().toISOString() : ticket.resolvedAt
          }
        : ticket
    );

    setTickets(updatedTickets);
    setShowEditDialog(false);
    setSelectedTicket(null);
    
    toast({
      title: "Thành công",
      description: "Đã cập nhật yêu cầu hỗ trợ",
    });
  };

  const deleteTicket = (ticketId: string) => {
    setTickets(tickets.filter(ticket => ticket.id !== ticketId));
    toast({
      title: "Thành công",
      description: "Đã xóa yêu cầu hỗ trợ",
    });
  };

  const addComment = () => {
    if (!newComment.trim() || !selectedTicket) return;

    const comment: Comment = {
      id: `C${String((selectedTicket.comments?.length || 0) + 1).padStart(3, '0')}`,
      content: newComment,
      author: 'Nhân viên hỗ trợ',
      createdAt: new Date().toISOString()
    };

    const updatedTickets = tickets.map(ticket =>
      ticket.id === selectedTicket.id
        ? {
            ...ticket,
            comments: [...(ticket.comments || []), comment],
            updatedAt: new Date().toISOString()
          }
        : ticket
    );

    setTickets(updatedTickets);
    setNewComment('');
    
    toast({
      title: "Thành công",
      description: "Đã thêm bình luận",
    });
  };

  const openEditDialog = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setEditTicket({
      title: ticket.title,
      description: ticket.description,
      category: ticket.category,
      priority: ticket.priority,
      status: ticket.status,
      relatedInfo: ticket.relatedInfo || '',
      assignedTo: ticket.assignedTo || ''
    });
    setShowEditDialog(true);
  };

  // Filter functions
  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || ticket.category === selectedCategory;
    const matchesStatus = selectedStatus === 'all' || ticket.status === selectedStatus;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Utility functions
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Hoàn thành':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Đang xử lý':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Mới':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Cao':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'Trung bình':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'Thấp':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Hoàn thành':
        return <CheckCircle className="h-4 w-4" />;
      case 'Đang xử lý':
        return <Clock className="h-4 w-4" />;
      case 'Mới':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">Hỗ trợ khách hàng</h1>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Tạo yêu cầu hỗ trợ
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Tạo yêu cầu hỗ trợ mới</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Tiêu đề *</Label>
                <Input
                  id="title"
                  placeholder="Mô tả ngắn gọn vấn đề của bạn"
                  value={newTicket.title}
                  onChange={(e) => setNewTicket({...newTicket, title: e.target.value})}
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Danh mục *</Label>
                  <Select value={newTicket.category} onValueChange={(value) => setNewTicket({...newTicket, category: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn danh mục" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Giao hàng">Giao hàng</SelectItem>
                      <SelectItem value="Kỹ thuật">Kỹ thuật</SelectItem>
                      <SelectItem value="Tài khoản">Tài khoản</SelectItem>
                      <SelectItem value="Thanh toán">Thanh toán</SelectItem>
                      <SelectItem value="Khác">Khác</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Mức độ ưu tiên *</Label>
                  <Select value={newTicket.priority} onValueChange={(value) => setNewTicket({...newTicket, priority: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn mức độ" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cao">Cao</SelectItem>
                      <SelectItem value="Trung bình">Trung bình</SelectItem>
                      <SelectItem value="Thấp">Thấp</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="relatedInfo">Thông tin liên quan</Label>
                <Input
                  id="relatedInfo"
                  placeholder="Mã đơn hàng, mã khách hàng, v.v..."
                  value={newTicket.relatedInfo}
                  onChange={(e) => setNewTicket({...newTicket, relatedInfo: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Mô tả chi tiết *</Label>
                <Textarea
                  id="description"
                  placeholder="Mô tả chi tiết vấn đề bạn gặp phải..."
                  rows={5}
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                  required
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Hủy
                </Button>
                <Button onClick={createTicket}>
                  <Save className="h-4 w-4 mr-2" />
                  Tạo yêu cầu
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Tổng yêu cầu</p>
                <p className="text-2xl font-bold">{tickets.length}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm">Đang xử lý</p>
                <p className="text-2xl font-bold">
                  {tickets.filter(t => t.status === 'Đang xử lý').length}
                </p>
              </div>
              <Clock className="h-8 w-8 text-yellow-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Hoàn thành</p>
                <p className="text-2xl font-bold">
                  {tickets.filter(t => t.status === 'Hoàn thành').length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Ưu tiên cao</p>
                <p className="text-2xl font-bold">
                  {tickets.filter(t => t.priority === 'Cao').length}
                </p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Tìm kiếm</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Tìm kiếm theo mã, tiêu đề..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Danh mục</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Tất cả danh mục" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả danh mục</SelectItem>
                  <SelectItem value="Giao hàng">Giao hàng</SelectItem>
                  <SelectItem value="Kỹ thuật">Kỹ thuật</SelectItem>
                  <SelectItem value="Tài khoản">Tài khoản</SelectItem>
                  <SelectItem value="Thanh toán">Thanh toán</SelectItem>
                  <SelectItem value="Khác">Khác</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Trạng thái</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Tất cả trạng thái" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả trạng thái</SelectItem>
                  <SelectItem value="Mới">Mới</SelectItem>
                  <SelectItem value="Đang xử lý">Đang xử lý</SelectItem>
                  <SelectItem value="Hoàn thành">Hoàn thành</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                  setSelectedStatus('all');
                }}
                className="w-full"
              >
                <X className="h-4 w-4 mr-2" />
                Xóa bộ lọc
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card>
        <CardHeader>
          <CardTitle>Danh sách yêu cầu hỗ trợ ({filteredTickets.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-full overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[120px]">Mã ticket</TableHead>
                  <TableHead className="min-w-[200px]">Thông tin yêu cầu</TableHead>
                  <TableHead className="min-w-[120px]">Danh mục</TableHead>
                  <TableHead className="min-w-[120px]">Độ ưu tiên</TableHead>
                  <TableHead className="min-w-[120px]">Trạng thái</TableHead>
                  <TableHead className="min-w-[120px]">Người tạo</TableHead>
                  <TableHead className="min-w-[120px]">Ngày tạo</TableHead>
                  <TableHead className="w-[200px] text-center">Thao tác</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTickets.map((ticket) => (
                  <TableRow key={ticket.id}>
                    <TableCell className="font-medium">{ticket.id}</TableCell>
                    <TableCell className="max-w-xs">
                      <div className="truncate font-medium">{ticket.title}</div>
                      <div className="text-sm text-muted-foreground truncate">
                        {ticket.description}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{ticket.category}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getPriorityColor(ticket.priority)}>
                        {ticket.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(ticket.status)}
                        <Badge className={getStatusColor(ticket.status)}>
                          {ticket.status}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>{ticket.createdBy}</TableCell>
                    <TableCell>{new Date(ticket.createdAt).toLocaleDateString('vi-VN')}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setShowDetailDialog(true);
                          }}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Xem
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openEditDialog(ticket)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Sửa
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Xác nhận xóa</AlertDialogTitle>
                              <AlertDialogDescription>
                                Bạn có chắc chắn muốn xóa yêu cầu hỗ trợ "{ticket.title}"? Hành động này không thể hoàn tác.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Hủy</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteTicket(ticket.id)}>
                                Xóa
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Chỉnh sửa yêu cầu hỗ trợ</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Tiêu đề *</Label>
              <Input
                id="edit-title"
                value={editTicket.title}
                onChange={(e) => setEditTicket({...editTicket, title: e.target.value})}
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Danh mục *</Label>
                <Select value={editTicket.category} onValueChange={(value) => setEditTicket({...editTicket, category: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Giao hàng">Giao hàng</SelectItem>
                    <SelectItem value="Kỹ thuật">Kỹ thuật</SelectItem>
                    <SelectItem value="Tài khoản">Tài khoản</SelectItem>
                    <SelectItem value="Thanh toán">Thanh toán</SelectItem>
                    <SelectItem value="Khác">Khác</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Mức độ ưu tiên *</Label>
                <Select value={editTicket.priority} onValueChange={(value) => setEditTicket({...editTicket, priority: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cao">Cao</SelectItem>
                    <SelectItem value="Trung bình">Trung bình</SelectItem>
                    <SelectItem value="Thấp">Thấp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Trạng thái *</Label>
                <Select value={editTicket.status} onValueChange={(value) => setEditTicket({...editTicket, status: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Mới">Mới</SelectItem>
                    <SelectItem value="Đang xử lý">Đang xử lý</SelectItem>
                    <SelectItem value="Hoàn thành">Hoàn thành</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Phân công cho</Label>
                <Input
                  placeholder="Nhân viên xử lý"
                  value={editTicket.assignedTo}
                  onChange={(e) => setEditTicket({...editTicket, assignedTo: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-relatedInfo">Thông tin liên quan</Label>
              <Input
                id="edit-relatedInfo"
                value={editTicket.relatedInfo}
                onChange={(e) => setEditTicket({...editTicket, relatedInfo: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">Mô tả chi tiết *</Label>
              <Textarea
                id="edit-description"
                rows={5}
                value={editTicket.description}
                onChange={(e) => setEditTicket({...editTicket, description: e.target.value})}
                required
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Hủy
              </Button>
              <Button onClick={updateTicket}>
                <Save className="h-4 w-4 mr-2" />
                Cập nhật
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Chi tiết yêu cầu hỗ trợ</DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Mã yêu cầu</Label>
                  <p className="font-semibold">{selectedTicket.id}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Người tạo</Label>
                  <p>{selectedTicket.createdBy}</p>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-muted-foreground">Tiêu đề</Label>
                <p className="font-medium">{selectedTicket.title}</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Danh mục</Label>
                  <Badge variant="outline" className="mt-1">{selectedTicket.category}</Badge>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Ưu tiên</Label>
                  <Badge className={`mt-1 ${getPriorityColor(selectedTicket.priority)}`}>
                    {selectedTicket.priority}
                  </Badge>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Trạng thái</Label>
                  <div className="flex items-center gap-2 mt-1">
                    {getStatusIcon(selectedTicket.status)}
                    <Badge className={getStatusColor(selectedTicket.status)}>
                      {selectedTicket.status}
                    </Badge>
                  </div>
                </div>
              </div>

              {selectedTicket.assignedTo && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Phân công cho</Label>
                  <p className="font-medium text-blue-600">{selectedTicket.assignedTo}</p>
                </div>
              )}

              {selectedTicket.relatedInfo && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Thông tin liên quan</Label>
                  <p>{selectedTicket.relatedInfo}</p>
                </div>
              )}

              <div>
                <Label className="text-sm font-medium text-muted-foreground">Mô tả chi tiết</Label>
                <p className="text-sm bg-muted p-3 rounded-md mt-1">{selectedTicket.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Ngày tạo</Label>
                  <p>{new Date(selectedTicket.createdAt).toLocaleString('vi-VN')}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Cập nhật lần cuối</Label>
                  <p>{new Date(selectedTicket.updatedAt).toLocaleString('vi-VN')}</p>
                </div>
              </div>

              {selectedTicket.resolvedAt && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Ngày giải quyết</Label>
                  <p className="text-green-600 font-medium">{new Date(selectedTicket.resolvedAt).toLocaleString('vi-VN')}</p>
                </div>
              )}

              {/* Comments Section */}
              <div className="space-y-4">
                <Label className="text-sm font-medium">Bình luận ({selectedTicket.comments?.length || 0})</Label>
                
                {selectedTicket.comments && selectedTicket.comments.length > 0 && (
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {selectedTicket.comments.map((comment) => (
                      <div key={comment.id} className="bg-muted p-3 rounded-md">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">{comment.author}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(comment.createdAt).toLocaleString('vi-VN')}
                          </span>
                        </div>
                        <p className="text-sm">{comment.content}</p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="new-comment">Thêm bình luận</Label>
                  <div className="flex gap-2">
                    <Textarea
                      id="new-comment"
                      placeholder="Nhập bình luận của bạn..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      className="flex-1"
                    />
                    <Button onClick={addComment} disabled={!newComment.trim()}>
                      Gửi
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Support; 